<template>
     <div ref="carouselRoot" :id="parentId" class="listing-collections-carousel"
       :style="{ '--carousel-controls-top': `${controlsTop}px` }">
      <Swiper
       v-if="isDataReady"
        class="listing-swiper"
        :class="{'cs_listing_slide': dataListingExtended.length < slidesPerView}"
        :slides-per-view="slidesPerView"
        :slides-per-group="slidesPerGroup"
        :spaceBetween="10"
        :modules="carouselModules"
        :freeMode="true"
        :breakpoints="breakpoints"
        :loop="loop"
        :loopFillGroupWithBlank="true"
        :navigation="{
            nextEl: `#${parentId} .swiper-button-next-list`,
            prevEl: `#${parentId} .swiper-button-prev-list`,
        }"
        @swiper="schedulePositionUpdate"
        @resize="schedulePositionUpdate"
        @breakpoint="schedulePositionUpdate"
      >
        <SwiperSlide  v-for="item in dataListingExtended" :key="item.id" v-bind="item.data_api ? { 'data-item': item.data_api } : {}" :class="{ 'item-unbxd': item.data_api }">
          <ListingItem
            :item="item"
            :whistlist="is_whistlist ?? true"
            :background_item="background_item ?? 'transparent'"
            :min_max="min_max ?? false"
            :hover_zoom="hover_zoom ?? false"
          />
        </SwiperSlide>
      </Swiper>

      <div class="box-controls" :style="{ visibility: swiperLoaded ? 'visible' : 'hidden', opacity: swiperLoaded ? 1 : 0 }" v-if="dataListingExtended.length > slidesPerView">
        <div class="box-btn">
          <div class="swiper-button swiper-button-prev-list">
            <svg
              fill="#000000"
              viewBox="0 0 24.00 24.00"
              id="right-2"
              data-name="Flat Line"
              xmlns="http://www.w3.org/2000/svg"
              class="icon flat-line"
              transform="rotate(180)"
            >
              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
              <g
                id="SVGRepo_tracerCarrier"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke="#CCCCCC"
                stroke-width="0.048"
              ></g>
              <g id="SVGRepo_iconCarrier">
                <polyline
                  id="primary"
                  points="7.5 3 16.5 12 7.5 21"
                  style="
                    fill: none;
                    stroke: rgb(0, 0, 0);
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    stroke-width: 0.816;
                  "
                ></polyline>
              </g>
            </svg>
          </div>
        </div>
        <div class="box-btn">
          <div class="swiper-button swiper-button-next-list">
            <svg
              fill="#000000"
              viewBox="0 0 24.00 24.00"
              id="right-2"
              data-name="Flat Line"
              xmlns="http://www.w3.org/2000/svg"
              class="icon flat-line"
            >
              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
              <g
                id="SVGRepo_tracerCarrier"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke="#CCCCCC"
                stroke-width="0.048"
              ></g>
              <g id="SVGRepo_iconCarrier">
                <polyline
                  id="primary"
                  points="7.5 3 16.5 12 7.5 21"
                  style="
                    fill: none;
                    stroke: rgb(0, 0, 0);
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    stroke-width: 0.816;
                  "
                ></polyline>
              </g>
            </svg>
          </div>
        </div>
      </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, onBeforeUnmount, computed } from 'vue';
import ListingItem from "~/components/Product/ProductItem.vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { FreeMode } from "swiper/modules";
import 'swiper/css/free-mode';
import "swiper/css";
import "swiper/css/navigation";
const carouselRoot = ref(null);
const controlsTop = ref(0);
const isDataReady = computed(() => Boolean(props.dataListing?.length));
const swiperLoaded = ref(false);
const props = defineProps({
        categories: Array,
        dataListing: Array,
        breakpoints: Object,
        slidesPerView: Number,
        modules: Array,
        loop: Boolean,
        navigation: Boolean,
        slidesPerGroup: Number,
        parentId: String,
        background_item: {
          type: String,
          default: 'transparent'
        },
        is_whistlist: {
          type: Boolean,
          default: true
        },
        min_max: {
          type: Boolean,
          default: false
        },
        hover_zoom: {
          type: Boolean,
          default: false
        },
        experienceImpression: {
          type: Object,
          default: false,
        }
    });
    let carouselModules = props.modules && Array.isArray(props.modules) ? [...props.modules] : [];
    if (!carouselModules.includes(FreeMode)) {
      carouselModules.push(FreeMode);
    }
    const navigationConfig = computed(() => ({
        nextEl: `#${props.parentId} .swiper-button-next-list`,
        prevEl: `#${props.parentId} .swiper-button-prev-list`,
    }));
    const dataListingExtended = computed(() => {
        const totalItemsNeeded = props.slidesPerView * 2;
        if (!props.loop || props.dataListing.length >= props.slidesPerView * 2) {
          return props.dataListing;
        }
        const extendedList = [...props.dataListing];
        while (extendedList.length < props.slidesPerView * 2) {
          extendedList.push(...props.dataListing.slice(0, props.slidesPerView * 2 - extendedList.length));
        }
        return extendedList;
    });
    let resizeObserver;
    let observedMedia;
    let positionFrame = null;

    const updateButtonPosition = () => {
      positionFrame = null;
      const root = carouselRoot.value;
      const media = root?.querySelector('.listing-item__media');

      if (media !== observedMedia) {
        if (observedMedia) resizeObserver?.unobserve(observedMedia);
        observedMedia = media;
        if (media) resizeObserver?.observe(media);
      }

      const mediaRect = media?.getBoundingClientRect();
      // A hidden tab has no measurable image box. Wait for ResizeObserver
      // to report its visible size instead of positioning every carousel at -10px.
      if (!root || !mediaRect?.height || !mediaRect.width) {
        swiperLoaded.value = false;
        return;
      }

      controlsTop.value = mediaRect.top - root.getBoundingClientRect().top
        - root.clientTop + root.scrollTop + mediaRect.height / 2;
      swiperLoaded.value = true;
    };

    function schedulePositionUpdate() {
      if (typeof window === 'undefined' || positionFrame !== null) return;
      positionFrame = window.requestAnimationFrame(updateButtonPosition);
    }

    watch(() => props.dataListing, schedulePositionUpdate, { flush: 'post' });

    onMounted(() => {
      resizeObserver = new ResizeObserver(schedulePositionUpdate);
      resizeObserver.observe(carouselRoot.value);
      window.addEventListener('resize', schedulePositionUpdate);
      schedulePositionUpdate();
    });

    onBeforeUnmount(() => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', schedulePositionUpdate);
      if (positionFrame !== null) window.cancelAnimationFrame(positionFrame);
    });
</script>

<style scoped>
.listing-collections-carousel > .box-controls > .box-btn > .swiper-button {
  top: var(--carousel-controls-top);
  margin-top: 0;
  transform: translateY(-50%);
}
</style>

<style lang="scss">
@import '@/assets/scss/components/sale-campaign/_carousel-production.scss';
.custom-slider-home-pr {
        .swiper.listing-swiper {
            padding: 20px 0;
            @media (max-width: 767.98px) {
                padding: 25px 0;
                padding-top: 15px;
            }
        }
        .swiper-button-lock {
            display: block !important;
        }
        .listing-collections-carousel {
          .box-controls {
            @media (max-width: 575.98px) {
              display: none;
            }
            .swiper-button-next-list {
              width: 27px;
              height: 44px;
              &.swiper-button-disabled {
                display: none;
              }
            }
            .swiper-button-prev-list {
              width: 27px;
              height: 44px;
              &.swiper-button-disabled {
                display: none;
              }
            }
          }
        }
        &.custom-cny-page {
            .swiper.listing-swiper {
              @media (max-width: 991.98px) {
                  padding: 10px 0;
              }
          }
        }
    }
</style>
